/**
 * useRecommendations – React hook that watches the current route and DOM
 * for changes and returns the live list of context-aware recommendations.
 *
 * Performance notes:
 *   - Context extraction runs on mount + on route change + on a debounced
 *     MutationObserver tick (so KPIs/tables that finish loading are picked
 *     up automatically).
 *   - Results are memoised; consumers only re-render when the action list
 *     actually changes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { extractUIContext, type UIContext } from '../services/UIContextExtractor';
import {
  generateRecommendations,
  type Recommendation,
} from '../services/RecommendationEngine';

const DEBOUNCE_MS = 350;

export interface UseRecommendationsResult {
  context: UIContext;
  recommendations: Recommendation[];
  refresh: () => void;
}

export function useRecommendations(): UseRecommendationsResult {
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.programRoleName ?? user?.role;

  const [tick, setTick] = useState(0);
  const debounceRef = useRef<number | null>(null);

  const refresh = () => setTick((t) => t + 1);

  // Re-scan when route changes.
  useEffect(() => {
    refresh();
  }, [location.pathname]);

  // Re-scan when the DOM settles after async data loads.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        refresh();
      }, DEBOUNCE_MS);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-kpi-value', 'data-pending-tasks', 'data-sla-breached', 'data-total-records'],
    });

    return () => {
      observer.disconnect();
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const context = useMemo(
    () => extractUIContext({ pathname: location.pathname, role }),
    // tick forces re-extraction without creating extra deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, role, tick],
  );

  const recommendations = useMemo(() => generateRecommendations(context), [context]);

  return { context, recommendations, refresh };
}
